import type { Contact, Conversation, DisappearTimerSec, MessageEvent } from "@veto/domain";
import { acceptMessageRequest } from "../messaging/addContact";
import { confirmAction, confirmClearChat } from "../components/ConfirmDialog";
import {
  archiveConversation,
  blockContact,
  markConversationRead,
  markConversationUnread,
  toggleMutedConversation,
  togglePinnedConversation,
  unarchiveConversation,
  unblockContact,
} from "@veto/domain";
import {
  clearConversationMessages,
  delMeta,
  deleteConversationData,
  getContact,
  getMeta,
  saveContact,
  setMeta,
  upsertConversation,
} from "@veto/storage";

export type ChatRowAction =
  "pin" | "mute" | "archive" | "block" | "chat-lock" | "disappear" | "read" | "clear" | "delete";

/** Local chat-lock controls the chrome layer needs (backed by useChatLockSession). */
export type ChatLockControls = {
  has: (conversationId: string) => Promise<boolean>;
  enable: (conversationId: string, pin: string) => Promise<void>;
  /** False when the chat wasn't opened with its PIN in this session. */
  disable: (conversationId: string) => Promise<boolean>;
};

export function createConversationChrome(deps: {
  activeId: string | null;
  activeConv: Conversation | null;
  activeContact: Contact | null;
  chatLock: ChatLockControls;
  setDisappearTimer: (s: DisappearTimerSec) => void;
  setToast: (t: string | null) => void;
  setActiveId: (id: string | null) => void;
  /** Clearing the open chat's message state must not leave stale bubbles on screen. */
  setMessages: (m: MessageEvent[]) => void;
  /** Opens the PIN modal (replaces the old window.prompt flow — native-shell safe). */
  requestChatLockEnable: (conversationId: string) => void;
  refresh: () => Promise<void>;
  /** Live ratchet sessions — delete must drop the in-memory entry or the next
   * send re-seals under a generation the durable layer no longer knows (fork). */
  onConversationDeleted: (conversationId: string) => void;
}) {
  /** Message request declined: optionally block, then purge chat + session + outbox. */
  function declineRequest(conv: Conversation, contact: Contact | null, block: boolean) {
    void (async () => {
      if (block && contact)
        await saveContact(blockContact({ ...contact, requestState: "pending" }));
      await deleteConversationData(conv.id);
      deps.onConversationDeleted(conv.id);
      if (deps.activeId === conv.id) {
        deps.setActiveId(null);
        deps.setMessages([]);
      }
      await deps.refresh();
      deps.setToast(block ? `${conv.title} blocked` : "Request deleted");
    })();
  }

  async function deleteChat(conv: Conversation) {
    const ok = await confirmAction({
      title: `Delete chat with ${conv.title}?`,
      body: "Messages are removed from this device only. Your contact keeps their copy.",
      action: "Delete",
    });
    if (!ok) return;
    void deleteConversationData(conv.id).then(async () => {
      // Drop the live WASM ratchet too — the durable session is gone; keeping
      // the in-memory one re-seals future sends under a generation the peer
      // (and a future re-add) cannot open.
      deps.onConversationDeleted(conv.id);
      if (deps.activeId === conv.id) {
        deps.setActiveId(null);
        deps.setMessages([]);
      }
      await deps.refresh();
      deps.setToast("Chat deleted");
    });
  }

  async function forgetConversation(conversationId: string) {
    await deleteConversationData(conversationId);
    await delMeta(`group.pending.${conversationId}`);
    deps.onConversationDeleted(conversationId);
    if (deps.activeId === conversationId) {
      deps.setActiveId(null);
      deps.setMessages([]);
    }
    await deps.refresh();
  }

  /** Leave a group: one dialog and wording everywhere (phone parity). */
  async function leaveGroup(conv: Conversation) {
    const ok = await confirmAction({
      title: `Leave ${conv.title}?`,
      body: "The group and its messages are removed from this device. Members aren't told; an admin can remove you so you stop receiving its messages.",
      action: "Leave",
    });
    if (!ok) return;
    await forgetConversation(conv.id);
    deps.setToast(`You left ${conv.title}`);
  }

  /** Block (with the shared confirmation) or unblock, with the same feedback everywhere. */
  async function toggleBlock(contact: Contact) {
    if (
      !contact.blockedAt &&
      !(await confirmAction({
        title: `Block ${contact.displayName}?`,
        body: "They can't reach you, and nothing they send is processed. You can unblock them later.",
        action: "Block",
      }))
    ) {
      return;
    }
    const next = contact.blockedAt ? unblockContact(contact) : blockContact(contact);
    await saveContact(next);
    await deps.refresh();
    deps.setToast(
      next.blockedAt ? `${contact.displayName} blocked` : `${contact.displayName} unblocked`,
    );
  }

  return {
    onAcceptRequest: () => {
      const contact = deps.activeContact;
      if (!contact) return;
      void acceptMessageRequest(contact.id).then(async () => {
        await deps.refresh();
        deps.setToast(`${contact.displayName} added — you can reply now`);
      });
    },
    onDeclineRequest: (block: boolean) => {
      if (!deps.activeConv) return;
      declineRequest(deps.activeConv, deps.activeContact, block);
    },
    onTogglePin: () => {
      if (!deps.activeConv) return;
      void upsertConversation(togglePinnedConversation(deps.activeConv)).then(deps.refresh);
    },
    onToggleMute: () => {
      if (!deps.activeConv) return;
      const next = toggleMutedConversation(deps.activeConv);
      void upsertConversation(next).then(async () => {
        await deps.refresh();
        deps.setToast(next.muted ? "Chat muted" : "Chat unmuted");
      });
    },
    onArchive: () => {
      if (!deps.activeConv) return;
      const next = deps.activeConv.archived
        ? unarchiveConversation(deps.activeConv)
        : archiveConversation(deps.activeConv);
      void upsertConversation(next).then(async () => {
        await deps.refresh();
        // Keep chat open after archive so Unarchive remains reachable in details
        // (archived rows are hidden from the main list — FR-CON list filter).
        deps.setToast(next.archived ? "Chat archived" : "Chat unarchived");
      });
    },
    onBlock: () => {
      if (!deps.activeContact) return;
      void toggleBlock(deps.activeContact);
    },
    onDisappearChange: (sec: DisappearTimerSec) => {
      deps.setDisappearTimer(sec);
      if (deps.activeId) void setMeta(`disappear.${deps.activeId}`, String(sec));
      deps.setToast(
        sec
          ? `Disappearing messages: ${sec}s (recipients may retain copies)`
          : "Disappearing messages off",
      );
    },
    /** Details-sheet entry: enable the lock (opens PIN modal) or remove an existing one. */
    onToggleChatLock: () => {
      if (!deps.activeId) return;
      void deps.chatLock.has(deps.activeId).then((locked) => {
        if (locked) {
          void deps.chatLock
            .disable(deps.activeId!)
            .then((ok) =>
              deps.setToast(
                ok ? "Chat unlocked" : "Open the chat with its PIN first, then unlock it",
              ),
            );
        } else {
          deps.requestChatLockEnable(deps.activeId!);
        }
      });
    },
    /** Details-sheet entry: wipe history but keep the conversation and its settings. */
    onClearActiveMessages: () => {
      if (!deps.activeConv) return;
      const conv = deps.activeConv;
      void confirmClearChat(conv.type === "group").then((ok) => {
        if (!ok) return;
        void clearConversationMessages(conv.id).then(async () => {
          if (deps.activeId === conv.id) deps.setMessages([]);
          await deps.refresh();
          deps.setToast("Chat cleared");
        });
      });
    },
    /** Details-sheet entry: delete the whole conversation (same flow as the row menu). */
    onDeleteActiveChat: () => {
      if (!deps.activeConv) return;
      if (deps.activeConv.type === "group") void leaveGroup(deps.activeConv);
      else void deleteChat(deps.activeConv);
    },
    /** Report & block: block without a second dialog (the report sheet already asked). */
    onReportBlock: async (contact: Contact, fromRequest: boolean) => {
      if (fromRequest && deps.activeConv) {
        declineRequest(deps.activeConv, contact, true);
        return;
      }
      if (!contact.blockedAt) await saveContact(blockContact(contact));
      await deps.refresh();
      deps.setToast(`${contact.displayName} is blocked. Send the email to finish your report.`);
    },
    /** Group invitation (review M1): join it. */
    onAcceptGroupInvite: () => {
      const conv = deps.activeConv;
      if (!conv) return;
      void (async () => {
        await delMeta(`group.pending.${conv.id}`);
        await upsertConversation({ ...conv, requestPending: false });
        await deps.refresh();
        deps.setToast(`You joined ${conv.title}`);
      })();
    },
    /** Group invitation: decline (the group is forgotten), optionally blocking whoever sent it. */
    onDeclineGroupInvite: (block: boolean) => {
      const conv = deps.activeConv;
      if (!conv) return;
      void (async () => {
        const from = await getMeta(`group.pending.${conv.id}`);
        if (block && from) {
          const inviter = await getContact(from);
          if (inviter && !inviter.blockedAt) await saveContact(blockContact(inviter));
        }
        await forgetConversation(conv.id);
        deps.setToast(block ? "Invitation declined, sender blocked" : "Invitation declined");
      })();
    },
    /** FR-CON-004: block is a contact-level action, so groups simply omit it. */
    onChatLockedCheck: (conversationId: string) => deps.chatLock.has(conversationId),
    /** Per-chat disappear timer for the long-press menu (meta is the per-chat source of truth). */
    onChatDisappearGet: async (conversationId: string) => {
      const raw = await getMeta(`disappear.${conversationId}`);
      return Number(raw ?? 0) as DisappearTimerSec;
    },
    /** Row-level actions for the long-press / right-click chat menu (per conversation, not active). */
    onChatRowAction: (
      conv: Conversation,
      contact: Contact | null,
      action: ChatRowAction,
      value?: DisappearTimerSec,
    ) => {
      switch (action) {
        case "pin": {
          const next = togglePinnedConversation(conv);
          void upsertConversation(next).then(async () => {
            await deps.refresh();
            deps.setToast(next.pinned ? "Chat pinned" : "Chat unpinned");
          });
          break;
        }
        case "mute": {
          const next = toggleMutedConversation(conv);
          void upsertConversation(next).then(async () => {
            await deps.refresh();
            deps.setToast(next.muted ? "Chat muted" : "Chat unmuted");
          });
          break;
        }
        case "archive": {
          const next = conv.archived ? unarchiveConversation(conv) : archiveConversation(conv);
          void upsertConversation(next).then(async () => {
            await deps.refresh();
            deps.setToast(next.archived ? "Chat archived" : "Chat unarchived");
          });
          break;
        }
        case "block": {
          if (!contact) return;
          void toggleBlock(contact);
          break;
        }
        case "chat-lock": {
          void deps.chatLock.has(conv.id).then((locked) => {
            if (locked) {
              void deps.chatLock
                .disable(conv.id)
                .then((ok) =>
                  deps.setToast(
                    ok ? "Chat unlocked" : "Open the chat with its PIN first, then unlock it",
                  ),
                );
            } else {
              deps.requestChatLockEnable(conv.id);
            }
          });
          break;
        }
        case "disappear": {
          const sec = value ?? 0;
          void setMeta(`disappear.${conv.id}`, String(sec));
          if (deps.activeId === conv.id) deps.setDisappearTimer(sec);
          deps.setToast(
            sec
              ? `Disappearing messages: ${sec}s (recipients may retain copies)`
              : "Disappearing messages off",
          );
          break;
        }
        case "read": {
          const next = conv.unread > 0 ? markConversationRead(conv) : markConversationUnread(conv);
          void upsertConversation(next).then(async () => {
            await deps.refresh();
            deps.setToast(next.unread > 0 ? "Marked as unread" : "Marked as read");
          });
          break;
        }
        case "clear": {
          void confirmClearChat(conv.type === "group").then((ok) => {
            if (!ok) return;
            void clearConversationMessages(conv.id).then(async () => {
              if (deps.activeId === conv.id) deps.setMessages([]);
              await deps.refresh();
              deps.setToast("Chat cleared");
            });
          });
          break;
        }
        case "delete": {
          if (conv.type === "group") void leaveGroup(conv);
          else void deleteChat(conv);
          break;
        }
      }
    },
  };
}
